import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/thumbs';

export interface GalleryItem {
  url: string;
  type: 'image' | 'video';
}

interface ImageGalleryProps {
  images: GalleryItem[];
  height?: string;
}

const ImageGallery = ({ images, height = 'h-[340px]' }: ImageGalleryProps) => {
  const [thumbSwiper, setThumbSwiper] = useState<SwiperType | null>(null);
  const [mainSwiper, setMainSwiper] = useState<SwiperType | null>(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(images.length <= 1);

  return (
    <div className="w-full">
      {/* Main Image */}
      <div className="relative overflow-hidden rounded-xl">
        <Swiper
          modules={[Thumbs]}
          thumbs={{
            swiper: thumbSwiper && !thumbSwiper.destroyed ? thumbSwiper : null,
          }}
          onSwiper={(swiper) => {
            setMainSwiper(swiper);
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
          }}
          onSlideChange={(swiper) => {
            setActiveIndex(swiper.activeIndex);
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
          }}
        >
          {images.map((item, index) =>
            item.type === 'video' ? (
              <SwiperSlide key={index}>
                {/* biome-ignore lint/a11y/useMediaCaption: no caption/transcript data exists for WhatsApp media */}
                <video src={item.url} controls className={`w-full object-contain ${height}`} />
              </SwiperSlide>
            ) : (
              <SwiperSlide key={index}>
                <img src={item.url} alt="" className={`w-full object-contain ${height}`} />
              </SwiperSlide>
            )
          )}
        </Swiper>

        {/* Counter */}
        <div className="absolute right-3 top-3 z-20 rounded-full bg-black/60 px-3 py-1 text-white text-sm">
          {activeIndex + 1}/{images.length}
        </div>

        {/* Prev */}
        <button
          type="button"
          onClick={() => mainSwiper?.slidePrev()}
          disabled={isBeginning}
          className={`absolute left-4 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full transition
            ${
              isBeginning
                ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                : 'bg-blue-500 text-white shadow'
            }`}
        >
          <ChevronLeft size={22} />
        </button>

        {/* Next */}
        <button
          type="button"
          onClick={() => mainSwiper?.slideNext()}
          disabled={isEnd}
          className={`absolute right-4 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full transition
            ${
              isEnd ? 'cursor-not-allowed bg-blue-300 text-white' : 'bg-blue-500 text-white shadow'
            }`}
        >
          <ChevronRight size={22} />
        </button>
      </div>

      {/* Thumbnail */}
      <div className="mt-4 flex justify-center">
        <Swiper
          modules={[Thumbs]}
          onSwiper={setThumbSwiper}
          watchSlidesProgress
          slidesPerView="auto"
          centeredSlides={false}
          spaceBetween={10}
          className="!w-auto"
        >
          {images.map((item, index) => {
            const thumbClassName = `
                  h-16
                  w-16
                  rounded-lg
                  object-contain
                  cursor-pointer
                  border-2
                  transition-all
                  ${
                    activeIndex === index
                      ? 'border-blue-500 opacity-100'
                      : 'border-transparent opacity-50'
                  }
                `;
            return (
              <SwiperSlide key={index} className="!w-[64px]">
                {item.type === 'video' ? (
                  <video src={item.url} muted className={thumbClassName} />
                ) : (
                  <img src={item.url} alt="" className={thumbClassName} />
                )}
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </div>
  );
};

export default ImageGallery;
